'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSparkles } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { useHelpWidget } from './HelpWidgetContext';
import HelpHeader from './components/HelpHeader';
import HelpMainMenu from './components/HelpMainMenu';
import HelpAIAssistant from './components/HelpAIAssistant';
import HelpOrderStatus from './components/HelpOrderStatus';
import HelpSwatchSamples from './components/HelpSwatchSamples';
import HelpSalesAssistance from './components/HelpSalesAssistance';
import HelpTradeProgram from './components/HelpTradeProgram';
import HelpContactForm from './components/HelpContactForm';
const panelTitles = {
  main: 'Welcome to hive',
  ai_assistant: 'Shopping Assistant',
  order_status: 'Order status',
  swatch_samples: 'Swatch samples',
  sales_assistance: 'Sales assistance',
  trade_program: 'Trade program',
  contact: 'Contact'
};

// Duration of the width transition in ms
const TRANSITION_DURATION = 800;
const HelpWidget = () => {
  const $ = _c(60);
  const {
    isOpen,
    currentPanel,
    closeWidget,
    minimizeWidget,
    setPanel
  } = useHelpWidget();
  const widgetRef = useRef(null);
  const [renderedPanel, setRenderedPanel] = useState(currentPanel);
  const [contentVisible, setContentVisible] = useState(true);
  const prevPanelRef = useRef(currentPanel);
  let t0;
  let t1;
  if ($[0] !== currentPanel) {
    t0 = () => {
      const wasAI = prevPanelRef.current === "ai_assistant";
      const isAI = currentPanel === "ai_assistant";
      if (wasAI !== isAI) {
        setContentVisible(false);
        const timer = setTimeout(() => {
          setRenderedPanel(currentPanel);
          requestAnimationFrame(() => {
            setContentVisible(true);
          });
        }, TRANSITION_DURATION);
        prevPanelRef.current = currentPanel;
        return () => clearTimeout(timer);
      }
      setRenderedPanel(currentPanel);
      prevPanelRef.current = currentPanel;
    };
    t1 = [currentPanel];
    $[0] = currentPanel;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== currentPanel) {
    t2 = () => {
      if (typeof window === "undefined") {
        return 320;
      }
      const vw = window.innerWidth;
      if (currentPanel !== "ai_assistant") {
        return 320;
      }
      if (vw >= 1280) {
        return Math.min(vw * 0.4, 700);
      }
      if (vw >= 1024) {
        return Math.min(vw * 0.5, 700);
      }
      if (vw >= 768) {
        return Math.min(vw * 0.7, 700);
      }
      return vw - 32;
    };
    $[3] = currentPanel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const getWidth = t2;
  const [width, setWidth] = useState(320);
  let t3;
  if ($[5] !== getWidth) {
    t3 = () => {
      setWidth(getWidth());
      const handleResize = () => setWidth(getWidth());
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    $[5] = getWidth;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== currentPanel) {
    t4 = [currentPanel];
    $[7] = currentPanel;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== closeWidget || $[10] !== isOpen) {
    t5 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape" && isOpen) {
          closeWidget();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [isOpen, closeWidget];
    $[9] = closeWidget;
    $[10] = isOpen;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[13] !== isOpen) {
    t7 = () => {
      if (isOpen && widgetRef.current) {
        const focusable = widgetRef.current.querySelector("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
        if (focusable) {
          focusable.focus();
        }
      }
    };
    t8 = [isOpen];
    $[13] = isOpen;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  if (!isOpen) {
    return null;
  }
  const showBack = currentPanel !== "main";
  const title = panelTitles[currentPanel] || "welcome to hive";
  const isAIAssistant = currentPanel === "ai_assistant";
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FontAwesomeIcon icon={faXmark} className="text-lg" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== closeWidget) {
    t10 = <button type="button" onClick={closeWidget} className="absolute -top-4 -right-4 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-lg text-dark hover:text-primary transition-colors z-10" aria-label="Close">{t9}</button>;
    $[17] = closeWidget;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = `${width}px`;
  let t12;
  if ($[19] !== t11) {
    t12 = {
      width: t11,
      transition: `width ${TRANSITION_DURATION}ms cubic-bezier(0.0, 0.0, 0.2, 1)`
    };
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== setPanel) {
    t13 = () => setPanel("main");
    $[21] = setPanel;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = isAIAssistant ? faSparkles : null;
  let t15;
  if ($[23] !== isAIAssistant || $[24] !== minimizeWidget || $[25] !== showBack || $[26] !== t13 || $[27] !== t14 || $[28] !== title) {
    t15 = <HelpHeader title={title} showBack={showBack} onBack={t13} showMinimize={isAIAssistant} onMinimize={minimizeWidget} rightIcon={t14} />;
    $[23] = isAIAssistant;
    $[24] = minimizeWidget;
    $[25] = showBack;
    $[26] = t13;
    $[27] = t14;
    $[28] = title;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = `flex-1 overflow-y-auto scrollbar scrollbar-y transition-opacity duration-300 ${contentVisible ? "opacity-100" : "opacity-0"}`;
  let t17;
  if ($[30] !== renderedPanel) {
    t17 = renderedPanel === "main" && <HelpMainMenu />;
    $[30] = renderedPanel;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== renderedPanel) {
    t18 = renderedPanel === "ai_assistant" && <HelpAIAssistant />;
    $[32] = renderedPanel;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== renderedPanel) {
    t19 = renderedPanel === "order_status" && <HelpOrderStatus />;
    $[34] = renderedPanel;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== renderedPanel) {
    t20 = renderedPanel === "swatch_samples" && <HelpSwatchSamples />;
    $[36] = renderedPanel;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== renderedPanel) {
    t21 = renderedPanel === "sales_assistance" && <HelpSalesAssistance />;
    $[38] = renderedPanel;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== renderedPanel) {
    t22 = renderedPanel === "trade_program" && <HelpTradeProgram />;
    $[40] = renderedPanel;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== renderedPanel) {
    t23 = renderedPanel === "contact" && <HelpContactForm />;
    $[42] = renderedPanel;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t16 || $[45] !== t17 || $[46] !== t18 || $[47] !== t19 || $[48] !== t20 || $[49] !== t21 || $[50] !== t22 || $[51] !== t23) {
    t24 = <div className={t16}>{t17}{t18}{t19}{t20}{t21}{t22}{t23}</div>;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t12 || $[54] !== t15 || $[55] !== t24) {
    t25 = <div ref={widgetRef} className="h-[600px] max-h-[calc(100vh-2rem)] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden animate-slide-up" style={t12} role="dialog" aria-modal="true" aria-labelledby="help-widget-title">{t15}{t24}</div>;
    $[53] = t12;
    $[54] = t15;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t10 || $[58] !== t25) {
    t26 = <div className="fixed bottom-4 right-[var(--site-padding)] z-[1000]">{t10}{t25}</div>;
    $[57] = t10;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  return t26;
};
export default HelpWidget;